import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { startOAuth } from "../lib/api";
import "./auth-dialog.css";

type AuthProvider = "github" | "linkedin";
const AuthDialogContext = createContext<{ openLogin: () => void } | null>(null);

function ProviderIcon({ provider }: { provider: AuthProvider }) {
  return provider === "github" ? (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M12 .9A11.1 11.1 0 0 0 8.49 22.53c.56.1.76-.24.76-.54v-2.07c-3.12.68-3.77-1.32-3.77-1.32-.5-1.3-1.24-1.64-1.24-1.64-1.02-.7.08-.68.08-.68 1.13.08 1.72 1.16 1.72 1.16 1 .1.86 2.02 3.24 1.12.1-.72.4-1.22.71-1.5-2.49-.28-5.1-1.24-5.1-5.55 0-1.23.44-2.23 1.15-3.02-.12-.28-.5-1.43.11-2.98 0 0 .94-.3 3.05 1.15A10.6 10.6 0 0 1 12 6.3c.95 0 1.9.13 2.79.38 2.12-1.44 3.05-1.15 3.05-1.15.61 1.55.23 2.7.12 2.98.71.79 1.14 1.79 1.14 3.02 0 4.32-2.62 5.27-5.12 5.55.4.35.76 1.03.76 2.08v2.83c0 .3.2.65.77.54A11.1 11.1 0 0 0 12 .9Z" /></svg>
  ) : (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M20.4 2H3.6A1.6 1.6 0 0 0 2 3.6v16.8A1.6 1.6 0 0 0 3.6 22h16.8a1.6 1.6 0 0 0 1.6-1.6V3.6A1.6 1.6 0 0 0 20.4 2ZM8 19H5V9h3v10ZM6.5 7.7a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4ZM19 19h-3v-5.4c0-1.3-.5-2-1.5-2s-1.7.8-1.7 2V19h-3V9h2.9v1.4a3.3 3.3 0 0 1 2.9-1.6c2.1 0 3.4 1.4 3.4 4.2v6Z" /></svg>
  );
}

export function AuthDialogProvider({ children }: { children: ReactNode }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const requestRef = useRef<AbortController | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [pending, setPending] = useState<AuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showDialog = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.showModal();
    dialog.querySelector<HTMLButtonElement>("[data-auth-primary]")?.focus();
  }, []);

  const openLogin = useCallback(() => {
    setError(null);
    showDialog();
  }, [showDialog]);
  const contextValue = useMemo(() => ({ openLogin }), [openLogin]);

  const cancelRequest = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    setPending(null);
  }, []);

  const closeDialog = useCallback(() => {
    cancelRequest();
    dialogRef.current?.close();
  }, [cancelRequest]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("login") !== "failed") return;
    url.searchParams.delete("login");
    window.history.replaceState(window.history.state, "", url);
    setError("Unable to sign in. Try again with GitHub or LinkedIn.");
    showDialog();
  }, [showDialog]);

  useEffect(() => () => { requestRef.current?.abort(); }, []);

  async function authenticate(provider: AuthProvider) {
    if (requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setPending(provider);
    setError(null);
    try {
      const { url } = await startOAuth(provider, controller.signal);
      if (!controller.signal.aborted && requestRef.current === controller && dialogRef.current?.open) {
        window.location.assign(url);
      }
    } catch (cause) {
      if (controller.signal.aborted || requestRef.current !== controller) return;
      setError(cause instanceof TypeError
        ? "Unable to connect. Check your connection and try again."
        : cause instanceof Error ? cause.message : "Unable to start authentication. Try again.");
      setPending(null);
      requestRef.current = null;
    }
  }

  return (
    <AuthDialogContext.Provider value={contextValue}>
      {children}
      <dialog className="auth-dialog" ref={dialogRef} aria-labelledby="auth-dialog-title" aria-describedby="auth-dialog-description" onCancel={(event) => { event.preventDefault(); closeDialog(); }} onClose={() => {
        cancelRequest();
        if (previousFocusRef.current?.isConnected) previousFocusRef.current.focus();
      }} onClick={(event) => { if (event.target === event.currentTarget) closeDialog(); }}>
        <div className="auth-dialog-content">
          <button className="auth-dialog-close" type="button" aria-label="Close sign-in" onClick={closeDialog}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg></button>
          <span className="auth-dialog-symbol" aria-hidden="true"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="m8 7-5 5 5 5m8-10 5 5-5 5M14 4l-4 16" /></svg></span>
          <span className="auth-dialog-eyebrow">Hello World.</span>
          <h2 id="auth-dialog-title">Join the conversation.</h2>
          <p id="auth-dialog-description">Save your likes, join the conversation and keep track of everything in your profile.</p>
          <div className="auth-dialog-providers" aria-busy={pending !== null}>
            <button type="button" data-auth-primary className="auth-provider auth-provider-github" onClick={() => void authenticate("github")} disabled={pending !== null}>{pending === "github" ? <span className="auth-dialog-spinner" aria-hidden="true" /> : <ProviderIcon provider="github" />}<span>{pending === "github" ? "Opening GitHub…" : "Continue with GitHub"}</span><span className="auth-provider-arrow" aria-hidden="true">→</span></button>
            <button type="button" className="auth-provider auth-provider-linkedin" onClick={() => void authenticate("linkedin")} disabled={pending !== null}>{pending === "linkedin" ? <span className="auth-dialog-spinner" aria-hidden="true" /> : <ProviderIcon provider="linkedin" />}<span>{pending === "linkedin" ? "Opening LinkedIn…" : "Continue with LinkedIn"}</span><span className="auth-provider-arrow" aria-hidden="true">→</span></button>
          </div>
          {error ? <p className="auth-dialog-error" role="alert">{error}</p> : null}
          <p className="auth-dialog-note">No extra password to remember.</p>
        </div>
      </dialog>
    </AuthDialogContext.Provider>
  );
}

export function useAuthDialog() {
  const context = useContext(AuthDialogContext);
  if (!context) throw new Error("useAuthDialog requires AuthDialogProvider.");
  return context;
}
